import { useEffect } from "react";

/**
 * Keeps the field being typed into visible above the on-screen keyboard (iPad / Android tablets).
 *
 * iPad Safari never resizes the page for the keyboard: it slides over the bottom half, and fixed
 * dialogs, drawers and full-height screens stay sized to the whole screen, so their lower fields
 * sit behind it. This hook measures the visible area with `window.visualViewport` and publishes:
 *
 *   --vv-top / --vv-height  visible area, in layout-viewport px (only while the keyboard is up)
 *   --kb-inset              keyboard height (0 when down)
 *   html[data-keyboard-open] while an editable field has focus and the keyboard covers the page
 *
 * index.css uses these to fit dialogs, sheets and drawers into the visible area and to give the
 * page room to scroll its last fields above the keyboard. On focus (touch devices only) the
 * field is then scrolled — inside its own scroll pane first, the page last — until it clears
 * the keyboard and the sticky top bar.
 */

const KEYBOARD_MIN_PX = 80; // smaller gaps are the iPad shortcut bar with a hardware keyboard
const MARGIN = 16;

function isEditable(el: Element | null): el is HTMLElement {
    if (!(el instanceof HTMLElement)) return false;
    if (el.isContentEditable) return true;
    if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
    if (el instanceof HTMLInputElement) {
        const nonText = ["button", "checkbox", "radio", "range", "color", "file", "submit", "reset", "image", "hidden"];
        return !nonText.includes(el.type) && !el.readOnly && !el.disabled;
    }
    return false;
}

function keyboardHeight(vv: VisualViewport): number {
    // Pinch-zoom also shrinks the visual viewport; that is not a keyboard
    if (vv.scale > 1.05) return 0;
    const kb = window.innerHeight - vv.height;
    return kb >= KEYBOARD_MIN_PX ? Math.round(kb) : 0;
}

function scrollableAncestors(el: HTMLElement): HTMLElement[] {
    const out: HTMLElement[] = [];
    // Includes #root, which is the page's scroll container (html/body/#root are height:100%)
    for (let p = el.parentElement; p; p = p.parentElement) {
        const oy = getComputedStyle(p).overflowY;
        if ((oy === "auto" || oy === "scroll" || oy === "overlay") && p.scrollHeight > p.clientHeight + 1) out.push(p);
    }
    return out;
}

/** Move `el` up (delta > 0) or down by scrolling its panes innermost-first, then the page. */
function scrollBy(el: HTMLElement, delta: number) {
    let remaining = delta;
    for (const sc of scrollableAncestors(el)) {
        if (Math.abs(remaining) < 1) return;
        // Don't push the field past the top (or bottom) edge of the pane it scrolls in
        const r = el.getBoundingClientRect();
        const box = sc.getBoundingClientRect();
        const step = remaining > 0
            ? Math.min(remaining, Math.max(0, r.top - box.top - MARGIN))
            : Math.max(remaining, Math.min(0, r.bottom - box.bottom + MARGIN));
        const before = sc.scrollTop;
        sc.scrollTop = before + step;
        remaining -= sc.scrollTop - before;
    }
    if (Math.abs(remaining) >= 1) window.scrollBy(0, remaining);
}

function revealFocused() {
    const el = document.activeElement;
    const vv = window.visualViewport;
    if (!isEditable(el) || !vv) return;

    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return;

    // Fields inside a dialog/sheet aren't under the page's sticky top bar
    const inOverlay = !!el.closest('[role="dialog"], [data-kb-overlay]');
    const topBar = inOverlay ? 0 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--topbar-height")) || 0;

    const visibleTop = Math.max(vv.offsetTop, topBar) + MARGIN;
    let visibleBottom = vv.offsetTop + vv.height - MARGIN;
    // A dialog's Save / Cancel row stays pinned above the keyboard; keep the field above it too
    const footer = el.closest('[role="dialog"]')?.querySelector("[data-kb-footer]");
    if (footer && !footer.contains(el)) visibleBottom = Math.min(visibleBottom, footer.getBoundingClientRect().top - 8);
    // A tall textarea only needs its first lines (where the caret usually is) in view
    const bottom = Math.min(r.bottom, r.top + 120);

    if (bottom > visibleBottom) scrollBy(el, bottom - visibleBottom);
    else if (r.top < visibleTop) scrollBy(el, r.top - visibleTop);
}

export function useOnScreenKeyboard() {
    useEffect(() => {
        const vv = window.visualViewport;
        if (!vv) return;

        const root = document.documentElement;
        const coarse = window.matchMedia("(pointer: coarse)");
        let revealTimer: number | undefined;
        let lastKb = 0;

        const scheduleReveal = (delay: number) => {
            if (!coarse.matches) return;
            window.clearTimeout(revealTimer);
            revealTimer = window.setTimeout(revealFocused, delay);
        };

        const update = () => {
            const kb = keyboardHeight(vv);
            const open = kb > 0 && isEditable(document.activeElement);

            if (open) {
                root.style.setProperty("--kb-inset", `${kb}px`);
                root.style.setProperty("--vv-top", `${Math.round(vv.offsetTop)}px`);
                root.style.setProperty("--vv-height", `${Math.round(vv.height)}px`);
                root.setAttribute("data-keyboard-open", "");
            } else if (root.hasAttribute("data-keyboard-open")) {
                root.style.removeProperty("--kb-inset");
                root.style.removeProperty("--vv-top");
                root.style.removeProperty("--vv-height");
                root.removeAttribute("data-keyboard-open");
            }

            // Keyboard appeared or changed height (e.g. switched to the number pad)
            if (open && kb !== lastKb) scheduleReveal(60);
            lastKb = open ? kb : 0;
        };

        const onFocusIn = (e: FocusEvent) => {
            if (!isEditable(e.target as Element)) return;
            update();
            // Keyboard already up (moving between fields): reveal now; otherwise wait for it to open
            scheduleReveal(lastKb ? 30 : 350);
        };
        // Focus leaving all fields closes the keyboard; let the viewport settle first
        const onFocusOut = () => window.setTimeout(update, 50);

        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
        document.addEventListener("focusin", onFocusIn);
        document.addEventListener("focusout", onFocusOut);
        update();

        return () => {
            vv.removeEventListener("resize", update);
            vv.removeEventListener("scroll", update);
            document.removeEventListener("focusin", onFocusIn);
            document.removeEventListener("focusout", onFocusOut);
            window.clearTimeout(revealTimer);
        };
    }, []);
}
